// Home uploads use the same parsers, atomic import and recoverable originals as the editor, one file at a time.
import { api } from './api.ts';
import { CLIENT_MAX_CELLS, MAX_COLS, MAX_ROWS } from '../../shared/ops.ts';
import { forgetFile, stashFile } from './stash.ts';
import type { ImportedBook } from './xlsx.ts';

export interface UploadResult { name: string; id?: string; error?: string; duplicate?: boolean; originalStored?: boolean; warning?: string }
export const FILE_ACCEPT = '.xlsx,.xls,.csv,.tsv,.txt';
export const isUploadFile = (file: File): boolean => /\.(xlsx|xls|csv|tsv|txt)$/i.test(file.name);

export function validateUploadFile(file: File): void {
  if (!isUploadFile(file)) throw new Error('지원하지 않는 파일 형식입니다');
  if (file.size > 50 * 1024 * 1024) throw new Error('50MB 이하의 파일만 가져올 수 있습니다');
}

export async function readUpload(file: File): Promise<ImportedBook> {
  if (/\.xlsx?$/i.test(file.name)) {
    const bytes = await file.arrayBuffer();
    return /\.xls$/i.test(file.name) ? (await import('./xls.ts')).importXls(bytes) : (await import('./xlsx.ts')).importXlsx(bytes);
  }
  const text = (await file.text()).replace(/^\ufeff/, '');
  const sep = /\.tsv$/i.test(file.name) || (!/\.csv$/i.test(file.name) && text.includes('\t')) ? '\t' : ',';
  const cells: ImportedBook['sheets'][number]['cells'] = [];
  let r = 0, c = 0, value = '', quoted = false;
  const cell = (): void => {
    if (r >= MAX_ROWS || c >= MAX_COLS || cells.length >= CLIENT_MAX_CELLS) throw new Error('가져올 수 있는 셀 수를 초과했습니다');
    if (value !== '') cells.push({ r, c, raw: value });
    value = ''; c++;
  };
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) { if (ch === '"') { if (text[i + 1] === '"') { value += '"'; i++; } else quoted = false; } else value += ch; }
    else if (ch === '"') quoted = true;
    else if (ch === sep) cell();
    else if (ch === '\n' || ch === '\r') { if (ch === '\r' && text[i + 1] === '\n') i++; cell(); r++; c = 0; }
    else value += ch;
  }
  if (value || c) cell();
  return { sheets: [{ name: '시트1', props: {}, cells, fmts: [] }], names: {}, functions: {}, savedImports: {} };
}

export async function uploadFiles(files: File[], userId: number, report: (result: UploadResult, index: number) => void, folderId?: string): Promise<void> {
  const seen = new Map<string, File[]>();
  for (const [index, file] of files.entries()) {
    const result: UploadResult = { name: file.name };
    const key = JSON.stringify([file.name, file.size, file.lastModified]);
    try {
      validateUploadFile(file);
      const matches = seen.get(key) ?? [];
      let duplicate = matches.includes(file);
      if (!duplicate && matches.length) {
        const bytes = new Uint8Array(await file.arrayBuffer());
        for (const other of matches) {
          const previous = new Uint8Array(await other.arrayBuffer());
          if (bytes.every((byte, i) => byte === previous[i])) { duplicate = true; break; }
        }
      }
      if (duplicate) { report({ ...result, duplicate: true }, index); continue; }
      seen.set(key, [...matches, file]);
      const title = file.name.replace(/\.[^.]+$/, '').slice(0, 200) || file.name.slice(0, 200);
      result.id = (await api<{ id: string }>('POST', '/api/docs', { title, folderId })).id;
      const stashKey = `${userId}:${result.id}`, requestId = crypto.randomUUID();
      // Do not begin an unattended upload unless its original can be recovered after a reload.
      try { await stashFile(stashKey, file, requestId); result.originalStored = true; }
      catch { throw new Error('복구용 원본을 보관하지 못해 업로드를 시작하지 않았습니다. 컴퓨터의 원본을 보관하고 다시 시도해 주세요.'); }
      const { importPayload, uploadImport } = await import('./xlsx.ts');
      const payload = importPayload(await readUpload(file), 'replace', title);
      payload.base = 0; payload.requestId = requestId;
      await uploadImport(result.id, payload);
      // A cleanup failure must not turn a confirmed save into a failed upload.
      try { await forgetFile(stashKey, requestId); result.originalStored = false; }
      catch { result.warning = '저장은 완료됐지만 복구용 원본 정리가 남았습니다. 문서를 열어 상단 안내에서 정리할 수 있습니다.'; }
    } catch (e) { result.error = (e as Error).message || '파일을 가져오지 못했습니다'; }
    report(result, index);
  }
}
